import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

type ParsedNotes = {
  plainText: string;
  webForm: Record<string, unknown> | null;
  webFormRaw: string | null;
};

type NotesViewModel = {
  plainText: string;
  additionalData: Record<string, unknown> | null;
  additionalRaw: string | null;
  serviceNotes: Array<{ serviceName: string; note: string }>;
  hasAnything: boolean;
};

@Component({
  selector: 'app-booking-dialog',
  templateUrl: './booking-dialog.component.html',
  styleUrls: ['./booking-dialog.component.scss']
})
export class BookingDialogComponent implements OnInit {
  bookings: any[] = [];
  private notesCache = new Map<string, ParsedNotes>();
  private notesVmCache = new Map<string, NotesViewModel>();

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public router: Router
  ) { }

  ngOnInit(): void {
    if (this.config.data) {
      this.config.data.bookings.sort((a: any, b: any) => {
        const nameA = a.customer && a.customer.name ? a.customer.name.toLowerCase() : '';
        const nameB = b.customer && b.customer.name ? b.customer.name.toLowerCase() : '';
        if (nameA < nameB) { return -1; }
        if (nameA > nameB) { return 1; }
        return 0;
      });
      this.bookings = this.config.data.bookings;
      console.log('bookings reservas', this.bookings);
    }
  }

  getNotesViewModel(booking: any): NotesViewModel {
    const key = this.buildNotesVmCacheKey(booking);
    const cached = this.notesVmCache.get(key);
    if (cached) {
      return cached;
    }

    const parsedFromNotes = this.getParsedNotes(booking?.notes);
    const parsedFromMeta = this.parseWebFormMeta(booking?.web_form_meta);

    const additionalData = parsedFromNotes.webForm ?? parsedFromMeta.webForm;
    const additionalRaw = parsedFromNotes.webFormRaw ?? parsedFromMeta.webFormRaw;

    const plainText = this.normalizePlainText(parsedFromNotes.plainText, booking?.notes);
    const serviceNotes = this.getServiceNotes(booking);

    const vm: NotesViewModel = {
      plainText,
      additionalData,
      additionalRaw,
      serviceNotes,
      hasAnything:
        Boolean(plainText) ||
        Boolean(additionalData) ||
        Boolean(additionalRaw) ||
        serviceNotes.length > 0,
    };

    this.notesVmCache.set(key, vm);
    return vm;
  }

  getParsedNotes(notes: unknown): ParsedNotes {
    const notesString = (typeof notes === 'string' ? notes : '')?.trim();
    if (!notesString) {
      return { plainText: '', webForm: null, webFormRaw: null };
    }

    const cached = this.notesCache.get(notesString);
    if (cached) {
      return cached;
    }

    const marker = 'WEB_FORM=';
    const markerIndex = notesString.indexOf(marker);
    if (markerIndex === -1) {
      const parsed: ParsedNotes = { plainText: notesString, webForm: null, webFormRaw: null };
      this.notesCache.set(notesString, parsed);
      return parsed;
    }

    const plainText = notesString.slice(0, markerIndex).trim();
    const afterMarker = notesString.slice(markerIndex + marker.length).trim();

    const extractedJson = this.extractJsonObject(afterMarker);
    if (!extractedJson) {
      const parsed: ParsedNotes = { plainText, webForm: null, webFormRaw: afterMarker || null };
      this.notesCache.set(notesString, parsed);
      return parsed;
    }

    try {
      const webForm = JSON.parse(extractedJson) as Record<string, unknown>;
      const parsed: ParsedNotes = { plainText, webForm, webFormRaw: null };
      this.notesCache.set(notesString, parsed);
      return parsed;
    } catch {
      const parsed: ParsedNotes = { plainText, webForm: null, webFormRaw: extractedJson };
      this.notesCache.set(notesString, parsed);
      return parsed;
    }
  }

  private normalizePlainText(parsedPlainText: string, originalNotes: unknown): string {
    if (parsedPlainText) return parsedPlainText;
    if (originalNotes === null || originalNotes === undefined) return '';
    if (typeof originalNotes === 'string') return originalNotes.trim();
    if (typeof originalNotes === 'object') {
      try {
        return JSON.stringify(originalNotes, null, 2);
      } catch {
        return String(originalNotes);
      }
    }
    return String(originalNotes).trim();
  }

  private parseWebFormMeta(meta: unknown): ParsedNotes {
    if (meta === null || meta === undefined) {
      return { plainText: '', webForm: null, webFormRaw: null };
    }

    if (typeof meta === 'string') {
      const metaString = meta.trim();
      if (!metaString) return { plainText: '', webForm: null, webFormRaw: null };
      const extracted = this.extractJsonObject(metaString) ?? metaString;
      try {
        const webForm = JSON.parse(extracted) as Record<string, unknown>;
        return { plainText: '', webForm, webFormRaw: null };
      } catch {
        return { plainText: '', webForm: null, webFormRaw: extracted };
      }
    }

    if (typeof meta === 'object') {
      return { plainText: '', webForm: meta as Record<string, unknown>, webFormRaw: null };
    }

    return { plainText: '', webForm: null, webFormRaw: String(meta) };
  }

  private getServiceNotes(booking: any): Array<{ serviceName: string; note: string }> {
    const services = Array.isArray(booking?.services) ? booking.services : [];
    return services
      .map((service: any) => {
        const note = typeof service?.notes === 'string' ? service.notes.trim() : '';
        if (!note) return null;
        const serviceName =
          service?.service_profile?.name ||
          service?.service_profile?.service?.name ||
          'Servicio';
        return { serviceName, note };
      })
      .filter(Boolean) as Array<{ serviceName: string; note: string }>;
  }

  private buildNotesVmCacheKey(booking: any): string {
    const id = booking?.id ?? 'no-id';
    const updatedAt = booking?.updated_at ?? booking?.updatedAt ?? '';
    const notes = typeof booking?.notes === 'string' ? booking.notes : JSON.stringify(booking?.notes ?? null);
    const meta = typeof booking?.web_form_meta === 'string' ? booking.web_form_meta : JSON.stringify(booking?.web_form_meta ?? null);
    return `${id}|${updatedAt}|${notes}|${meta}`;
  }

  formatWebFormValue(value: unknown): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (typeof value === 'string') return value.trim() === '' ? '-' : value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  formatWebFormKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b([a-z])/g, (m) => m.toUpperCase());
  }

  private extractJsonObject(input: string): string | null {
    if (!input) return null;

    const firstBrace = input.indexOf('{');
    const lastBrace = input.lastIndexOf('}');
    if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
      return null;
    }

    return input.slice(firstBrace, lastBrace + 1).trim();
  }

  returnAddressFromBooking(booking: any): string {
    return booking.office ? booking.office.name : '';
  }

  returnItemsAndDrumsByBooking(booking: any): string {
    console.log('booking returnitemsanddrums', booking);
    const seenIds = new Set();
    let servicesValidated = booking.services.filter((service: any) => {
      if (!seenIds.has(service.item_group_booking_service_id)) {
        seenIds.add(service.item_group_booking_service_id);
        return true;
      }
      return false;
    });
    let itemsHtml = servicesValidated
      .map((service: any) => {
        return service.items.map((item: any) => {
          return `<div class="whitespace-nowrap m-0">${item.name} x ${item.units}</div>`;
        }).join('');
      }).join('');

    console.log('itemsHtml returnitemsanddrums', itemsHtml);

    let titleDrums = `Bidones: <br/>`;
    let drumsArray: string[] = [];

    servicesValidated.forEach((service: any) => {
      if (service.drums) {
        const serviceDrums = JSON.parse(service.drums);
        if (Array.isArray(serviceDrums) && serviceDrums.length > 0) {
          drumsArray = [...drumsArray, ...serviceDrums];
        }
      }
    });

    let drumsHtml = drumsArray.length > 0 ? drumsArray.join(', ') : '';


    if (drumsHtml.trim() != '') {
      drumsHtml = titleDrums + `<div class="whitespace-nowrap">${drumsHtml}</div>`;
      return `${itemsHtml} <br/> ${drumsHtml}`;
    } else {
      return itemsHtml;
    }
  }

  returnTotalTickets(booking: any): number {
    return booking.services.reduce((total: number, service: any) => total + service.units, 0);
  }

  getStatusColor(booking: any): string {
    return booking.status ? booking.status.color : 'gray';
  }

  getStatusTextColor(booking: any): string {
    return booking.status ? booking.status.text_color : 'white';
  }

  getStatusName(booking: any): string {
    return booking.status ? booking.status.status : 'Unknown';
  }

  editBooking(booking: any): void {
    window.open(`/panel/bookings/edit/${booking.id}`, '_blank');
    // this.ref.close();
    // this.router.navigate([`/panel/bookings/edit/${booking.id}`]);
  }
}
